import React, { useCallback, useEffect, useRef, useState } from "react";
import "./InteractiveDemoConsole.css";

const INPUT_CAPACITY = 65536;
const PROGRAM_CAPACITY = 65536;
const PROGRAM_TIMEOUT = 500000;
let nextConsoleId = 0;

/** Executes trusted JavaScript programs in a fresh worker for each run. */
function InteractiveDemoConsole({ code, title = "Interactive demo console", theme = "default" }) {
  const workerRef = useRef(null);
  const timeoutRef = useRef(null);
  const waitingRef = useRef(false);
  const bottomRef = useRef(null);
  const [inputId] = useState(() => `interactive-demo-console-input-${++nextConsoleId}`);
  const [output, setOutput] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("loading");

  const stopWorker = useCallback(() => {
    workerRef.current?.terminate();
    workerRef.current = null;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    waitingRef.current = false;
  }, []);

  const startProgram = useCallback(() => {
    stopWorker();
    setOutput("");
    setInputValue("");
    setInputError("");
    setError("");
    setStatus("loading");
    try {
      if (typeof code !== "string") throw new TypeError("Program code must be a string.");
      if (new TextEncoder().encode(code).length > PROGRAM_CAPACITY) {
        throw new Error("The program is too large to run.");
      }
      const worker = new Worker(`${process.env.PUBLIC_URL}/interactive-demo-console/console-worker.js`);
      workerRef.current = worker;
      worker.onmessage = ({ data }) => {
        if (workerRef.current !== worker) return;
        switch (data.type) {
          case "ready": setStatus("running"); break;
          case "output": setOutput((previous) => previous + data.text); break;
          case "input": waitingRef.current = true; setStatus("input"); break;
          case "done":
          case "error":
            setError(data.message || "");
            setStatus(data.type === "done" ? "done" : "error");
            stopWorker();
            break;
          default: break;
        }
      };
      worker.onerror = (event) => {
        if (workerRef.current !== worker) return;
        setError(event.message || "The program worker could not start. Check the deployed worker file and Content Security Policy.");
        setStatus("error");
        stopWorker();
      };
      worker.postMessage({ type: "run", code });
      timeoutRef.current = setTimeout(() => {
        if (workerRef.current !== worker) return;
        setError("The program took too long to finish. Restart to run it again.");
        setStatus("error");
        stopWorker();
      }, PROGRAM_TIMEOUT);
    } catch (exception) {
      stopWorker();
      setError(exception.message);
      setStatus("error");
    }
  }, [code, stopWorker]);

  useEffect(() => {
    startProgram();
    return stopWorker;
  }, [startProgram, stopWorker]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [output, status]);

  const submitInput = (event) => {
    event.preventDefault();
    if (!waitingRef.current || !workerRef.current) return;
    const bytes = new TextEncoder().encode(inputValue);
    if (bytes.length > INPUT_CAPACITY) {
      setInputError("Please enter a shorter response (maximum 64 KiB).");
      return;
    }
    waitingRef.current = false;
    // Queue the echo before resuming the worker, preserving output order.
    setOutput((previous) => previous + inputValue + "\n");
    setInputValue("");
    setInputError("");
    setStatus("running");
    workerRef.current.postMessage({ type: "input", value: inputValue });
  };

  const programIsRunning = ["loading", "running", "input"].includes(status);
  const lines = output.split("\n");
  const tail = lines.pop();
  return (
    <section className="interactive-demo-console" data-theme={theme} aria-label={title}>
      <div className="interactive-demo-console__window">
        <div className="interactive-demo-console__bar">
          <span className="interactive-demo-console__program-name">{title}</span>
          <button className="interactive-demo-console__run" onClick={startProgram}>
            <span className="interactive-demo-console__run-icon" aria-hidden="true">↻</span>
            <span>{programIsRunning ? "Restart program" : "Run again"}</span>
          </button>
        </div>
        <div className="interactive-demo-console__body" aria-live="polite">
          {status === "loading" && <div className="interactive-demo-console__line interactive-demo-console__line--system"><i className="interactive-demo-console__spinner" aria-hidden="true" />Starting program…</div>}
          {lines.map((line, index) => <div className="interactive-demo-console__line" key={index}>{line || " "}</div>)}
          {status === "input" ? (
            <form className="interactive-demo-console__input" onSubmit={submitInput}>
              <label htmlFor={inputId}>{tail}</label>
              <input id={inputId} autoFocus autoComplete="off" autoCapitalize="none" spellCheck={false} enterKeyHint="send"
                value={inputValue} onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.nativeEvent.isComposing) submitInput(event);
                }} aria-label={tail || "Program input"} />
            </form>
          ) : tail ? <div className="interactive-demo-console__line">{tail}</div> : null}
          {(error || inputError) && <div role="alert" className="interactive-demo-console__line interactive-demo-console__line--error">{error || inputError}</div>}
          {status === "done" && <div className="interactive-demo-console__exit">✓ Program finished.</div>}
          <div ref={bottomRef} />
        </div>
      </div>
    </section>
  );
}

export default InteractiveDemoConsole;
